import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '../../common/react/CommonStyles';
import { cssClass, flexRowStyle } from '../../style';
import { SpaceContainerStyles } from '../../style/SpaceContainerStyles';

const cssPrefix = 'PageHeader';

const ninetyPercentWhite = 'rgba(255, 255, 255, 0.9)';
const twentytyPercentWhite = 'rgba(255, 255, 255, 0.2)';
const vizViewerLinkColor = ninetyPercentWhite;
const vizViewerLinkActiveColor = TabStyles.Colors.White;

export namespace PageHeaderStyles {
  const headerHeight = 52;
  export const height = `${headerHeight}px`;
  export const backButtonHeight = 26;

  const ContainerStyles = {
    display: 'flex',
    height: height,
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
  export const Container = cssClass(cssPrefix, 'container', ContainerStyles);

  export const ContainerVizViewer = cssClass(cssPrefix, 'container-viz-viewer', {
    ...ContainerStyles,
    backgroundColor: TabStyles.Colors.ApplicationBlue,
  });

  // we have a left region and right region so that we do not override the link styling inside the
  // alerts panel
  export const LeftRegionVizViewer = cssClass(cssPrefix, 'left-region-viz-viewer', {
    height: '100%',
    color: TabStyles.Colors.White,
    $nest: {  // for breadcrumbs
      'a': {
        color: vizViewerLinkColor,
        $nest: {
          '&:hover': {
            color: vizViewerLinkActiveColor
          },
          '&:active': {
            color: vizViewerLinkActiveColor
          }
        }
      }
    }
  });

  export const RightRegionVizViewer = cssClass(cssPrefix, 'right-region-viz-viewer', {
    height: '100%',
    // Since our page header isn't responsive the right region can expand beyond
    // the Pageheader width, so this ensures the page header background stays application blue
    backgroundColor: TabStyles.Colors.ApplicationBlue
  });

  export const Region = cssClass(cssPrefix, 'region', flexRowStyle, {
    height: '100%',
    marginLeft: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
    marginRight: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
  });

  export const RightItem = cssClass(cssPrefix, 'right-item', {
    $nest: {
      '&:not(:last-of-type)': {
        marginRight: 20
      }
    }
  });

  export const LeftItem = cssClass(cssPrefix, 'left-item', {
    $nest: {
      '&:not(:last-of-type)': {
        marginRight: 12
      }
    }
  });

  export const NavPanelButton = cssClass(cssPrefix, 'nav-panel-button', {
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  });

  const dividerHeight = 20;
  export const VizViewerDivider = cssClass(cssPrefix, 'viz-viewer-divider', {
    width: 1,
    height: dividerHeight,
    marginTop: (headerHeight - dividerHeight) / 2,
    marginRight: 11,  // the sapce between the divider and the eyeglass needs to be 20. The eyeglass
                      // icon has 9 px of padding, so we only need 11 here
    backgroundColor: 'rgba(255, 255, 255, .6)'
  });

  export const VizViewerButtons = cssClass(cssPrefix, 'viz-viewer-buttons', {
    ...CommonStyles.Layout.FlexBlockLeftAndCenter,
    fontWeight: 'normal',
  });

  export const BackButton: React.CSSProperties = {
    cursor: 'pointer',
    height: `${backButtonHeight}px`,
    marginRight: '8px',
    marginTop: '2px'
  };

  export const ButtonFocus: React.CSSProperties = {
    border: '1px solid ' + CommonStyles.Colors.FocusBorderBlue,
    boxShadow: '0 0 4px ' + TabStyles.Colors.Focus
  };

  export const FavoriteButtonFocusClass = cssClass(cssPrefix, 'favorite-button-focus', {
    $nest: {
      '&:focus': {
        ...ButtonFocus,
      }
    },
    paddingRight: '1px'  // favorite button has some padding inside the icon we need to make up for
  });

  export const BackButtonWrapperDiv: React.CSSProperties = {
    marginTop: `11px`
  };

  export const TextButtonStyle: React.CSSProperties = {
    color: ninetyPercentWhite,
    padding: TabStyles.Spacing.DefaultSpace,
    cursor: 'pointer',
    height: '20px',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
    marginRight: '0px'
  };

  export const TextButton = cssClass(cssPrefix, 'text-button', {
    ...TextButtonStyle,
    $nest: {
      '&:hover': {
        color: TabStyles.Colors.White,
        backgroundColor: twentytyPercentWhite
      }
    }
  });

  export const iconForTextButton: React.CSSProperties = {
    verticalAlign: 'middle',
    paddingRight: TabStyles.Spacing.DefaultSpace,
    paddingBottom: '1px',
    cursor: 'pointer'
  };

  export const iconOnlyButton: React.CSSProperties = {
    ...iconForTextButton,
    paddingRight: '0px',
    marginRight: '1px'
  };

  // This is so the links inside the data connections tooltip (on viz pages) can have the blue outline focus
  export const dataConnectionsTooltipClass = cssClass(cssPrefix, 'dataconnection-tooltip-links', {
    margin: 12,
    minWidth: 200,
    $nest: {
      'a': {
        width: 'calc(100% - 2px)',
        height: '19px',
        lineHeight: '17px',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        boxShadow: '0 0 4px transparent',
      },
      'a:focus': {
        borderColor: CommonStyles.Colors.FocusBorderBlue,
        boxShadow: '0 0 4px ' + TabStyles.Colors.Focus,
      }
    }
  });
}
